import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  type Chatbox,
  type ConversationBuilder,
  type Session,
} from 'talkjs/all';
import { Box, Text } from '@rsos/base-components';
import { CALL_OUT_OF_JURISDICTION } from '@rsos/constants/collaborator';
import useTF911LoggedInUser from '@rsos/hooks/talkjs/useTF911LoggedInUser';
import { useOpenChatTypes } from '@rsos/reduxDataStore/src/features/chats/hooks';
import { useGetSelectedCollaborator } from '@rsos/reduxDataStore/src/features/emergencyCalls/helpers';
import { CHAT_TAB } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import { setTempMessage } from '@rsos/reduxDataStore/src/features/outboundSms/outboundSmsSlice';
import { type PreconfiguredResponse } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesAPI';
import {
  useSelectedTextFrom911ConversationID,
  useSelectedTextFrom911Conversation,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import {
  TF911_CONVERSATION_ENDED,
  addParticipant,
  setConversationUnread,
  setPendingTF911Message,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import { buildDoNotTranslateSendOptions } from '@rsos/utils/SmartPhrases';
import { createTalkSession, createTalkUser } from '@rsos/utils/talkJS';
import { RootState } from '../../../../app';
import useTalkJSTheme from '../../../../hooks/useTalkJSTheme';
import SuggestedResponses from '../../../PreconfiguredMessages/SuggestedResponses';
import RequestLocation from '../../../RequestLocation';
import { useChatContext } from '../ChatContext';
import { OutgoingTranslationWarning } from '../SharedChatUI/OutgoingTranslationWarning';
import { useDoNotTranslateMessageField } from '../useDoNotTranslateMessageField';
import { useRequestLocation } from '../useRequestLocation';
import { useTrackSentLocationRequests } from '../useTrackSentLocationRequests';
import { ChatContainer } from './TF911.styles';
import {
  trackSendPreconfiguredResponse,
  trackViewAllPreconfiguredResponses,
} from './TF911ChatBoxTrackings';

interface TF911ChatProps {
  callerID: string;
  preconfiguredMessages: PreconfiguredResponse[];
  hasRequestLocationAccess: boolean;
}

const TF911Chat = ({
  callerID,
  preconfiguredMessages,
  hasRequestLocationAccess,
}: TF911ChatProps) => {
  const dispatch = useDispatch();
  const {
    isCollapsed: isChatboxCollapsed,
    setIsAllMessagesVisible,
    selectedPreconfiguredMessage,
    setSelectedPreconfiguredMessage,
    locationRequestPathway,
  } = useChatContext();
  const isTF911ChatOpen = useOpenChatTypes().textFrom911;
  const tempMessage = useSelector(
    (state: RootState) => state.outboundSms.tempMessage,
  );
  const tempMessageDoNotTranslate = useSelector(
    (state: RootState) => state.outboundSms.tempMessageDoNotTranslate,
  );
  const selectedTab = useSelector(
    (state: RootState) => state.eventPane.selectedTab,
  );
  const collaborator = useGetSelectedCollaborator();
  const isOutOfJurisdiction = collaborator.type === CALL_OUT_OF_JURISDICTION;

  const chatRef = useRef<HTMLDivElement>(null);

  const [chatSession, setChatSession] = useState<Session | null>(null);
  const [chatbox, setChatbox] = useState<Chatbox | null>(null);
  const [chatConversation, setChatConversation] =
    useState<ConversationBuilder | null>(null);

  const { protectPrefilledPhrases } = useDoNotTranslateMessageField(chatbox);

  const chatTheme = useTalkJSTheme('tf911');

  const conversationID = useSelectedTextFrom911ConversationID(callerID);

  const conversation = useSelectedTextFrom911Conversation(callerID);
  const hasUnreadMessages = conversation?.ui?.hasUnreadMessages;

  const isConversationEnded = useMemo(
    () => conversation?.data?.custom?.STATUS === TF911_CONVERSATION_ENDED,
    [conversation],
  );
  const pendingTF911Message = useSelector(
    (state: RootState) => state.textFrom911.pendingTF911Message,
  );
  const pendingTF911MessageDoNotTranslate = useSelector(
    (state: RootState) => state.textFrom911.pendingTF911MessageDoNotTranslate,
  );
  const selectedConversation = useSelector(
    (state: RootState) => state.textFrom911.selectedConversation,
  );

  const shouldShowChatButtons =
    !isConversationEnded &&
    !isChatboxCollapsed &&
    conversationID &&
    !isOutOfJurisdiction;

  const loggedInUser = useTF911LoggedInUser();

  const messageFieldVisibility = useMemo(() => {
    if (isOutOfJurisdiction) {
      return false;
    }

    return !isConversationEnded;
  }, [isConversationEnded, isOutOfJurisdiction]);

  // Mount the TalkJS session and chat container
  useEffect(() => {
    let session, chat;
    try {
      (async () => {
        session = await createTalkSession(loggedInUser);

        chat = session.createChatbox({
          theme: chatTheme,
          showChatHeader: false,
          messageField: {
            visible: messageFieldVisibility,
            placeholder: 'Type Message',
          },
        });

        chat.mount(chatRef.current);

        dispatch(addParticipant({ callerID, participant: loggedInUser }));

        setChatSession(session);

        setChatbox(chat);
      })();
    } catch (error) {
      if (session) {
        setChatbox(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatTheme, loggedInUser, dispatch]);

  // Set Conversation
  useEffect(() => {
    if (chatbox && chatSession && conversationID) {
      const conversation = chatSession.getOrCreateConversation(conversationID);
      if (pendingTF911Message && selectedConversation) {
        conversation.sendMessage(
          pendingTF911Message,
          buildDoNotTranslateSendOptions(pendingTF911MessageDoNotTranslate),
        );
        dispatch(setPendingTF911Message(null));
      }
      // TF911 Conversation Setup

      conversation.setParticipant(createTalkUser(loggedInUser));
      setChatConversation(conversation);
      chatbox.select(conversation);

      chatbox.messageField.setVisible(messageFieldVisibility);
    }
  }, [
    chatSession,
    chatbox,
    conversationID,
    dispatch,
    isConversationEnded,
    loggedInUser,
    messageFieldVisibility,
    pendingTF911Message,
    pendingTF911MessageDoNotTranslate,
    selectedConversation,
  ]);

  // Kept out of the conversation setup effect above, and unsubscribed on
  // cleanup: createTalkSession returns a process-wide singleton, so a handler
  // left behind by any of that effect's many dependencies changing would report
  // every later send a second time.
  useEffect(() => {
    if (!chatSession || !conversationID) return;

    const subscription = chatSession.onMessage(message => {
      const matchedMessage = preconfiguredMessages?.find(
        msg => msg.body === message.body,
      );

      if (matchedMessage) {
        trackSendPreconfiguredResponse(matchedMessage, conversationID);
      }
    });

    return () => subscription.unsubscribe();
  }, [chatSession, conversationID, preconfiguredMessages]);

  useTrackSentLocationRequests({
    chatSession,
    pathwayType: locationRequestPathway,
    callerID,
    conversationId: conversationID,
  });

  useEffect(() => {
    if (
      selectedTab === CHAT_TAB &&
      isTF911ChatOpen &&
      !isChatboxCollapsed &&
      conversationID &&
      chatSession &&
      chatbox &&
      hasUnreadMessages
    ) {
      dispatch(setConversationUnread({ callerID, hasUnreadMessages: false }));
    }
  }, [
    selectedTab,
    isTF911ChatOpen,
    conversationID,
    chatSession,
    chatbox,
    isChatboxCollapsed,
    hasUnreadMessages,
    callerID,
    dispatch,
  ]);

  // Set messageField text
  useEffect(() => {
    if (!chatbox || !chatConversation || isConversationEnded) return;

    if (selectedPreconfiguredMessage) {
      if (selectedPreconfiguredMessage.isSending) {
        chatConversation.sendMessage(
          selectedPreconfiguredMessage.message.body,
          buildDoNotTranslateSendOptions(
            selectedPreconfiguredMessage.message.resolvedPhrases,
          ),
        );
        chatbox.messageField.setText('');
      } else {
        chatbox.messageField.setText(selectedPreconfiguredMessage.message.body);
        protectPrefilledPhrases(
          selectedPreconfiguredMessage.message.resolvedPhrases,
        );
      }
      setSelectedPreconfiguredMessage(null);
    }
    if (tempMessage) {
      chatbox.messageField.setText(tempMessage);
      protectPrefilledPhrases(tempMessageDoNotTranslate);
      dispatch(setTempMessage(null));
    }
  }, [
    selectedPreconfiguredMessage,
    chatbox,
    chatConversation,
    isConversationEnded,
    protectPrefilledPhrases,
    tempMessage,
    tempMessageDoNotTranslate,
    setSelectedPreconfiguredMessage,
    dispatch,
  ]);

  const handleViewClick = () => {
    trackViewAllPreconfiguredResponses(conversationID);
    setIsAllMessagesVisible(true);
  };

  const { handleRequestLocation, locationError } = useRequestLocation({
    chat: chatbox,
    isConversationEnded,
    preconfiguredMessages,
    pathwayType: locationRequestPathway,
    conversationId: conversationID,
    protectPhrases: protectPrefilledPhrases,
  });

  return (
    <ChatContainer
      height="100%"
      display={!isChatboxCollapsed && conversationID ? 'flex' : 'none'}
      showBorder={!isChatboxCollapsed && conversationID}
      border="1px solid"
      borderRadius="0 5px 5px 0"
      borderColor="lightLineDivider"
    >
      <Box ref={chatRef} height="100%" />
      {shouldShowChatButtons && <OutgoingTranslationWarning />}
      {shouldShowChatButtons && (
        <>
          <Box width="100%" horizontal align="center">
            {hasRequestLocationAccess && (
              <RequestLocation onClick={handleRequestLocation} />
            )}
            <SuggestedResponses handleViewClick={handleViewClick} />
          </Box>
          {locationError && (
            <Text size="small" color="heavyDanger">
              Something went wrong, please try again
            </Text>
          )}
        </>
      )}
    </ChatContainer>
  );
};
export default TF911Chat;
